import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { IOfficeSelector, UserInfoService } from '@tt-chvac/core-sales-web';
import { UserService } from '../../../modules/jobs-list-master/services/user.service';
import { IDashboardPreference } from '../../model/dashboard-preferences';
import { GlobalFilterService } from '../../services/global-filter.service';
import { ITileInfo, TileService } from '../../services/tile.service';

@Component({
    selector: 'app-user-preferences',
    templateUrl: './user-preferences.component.html',
    standalone: false
})

export class UserPreferencesComponent implements OnInit {
  public userSelectableTiles: ITileInfo[]; // Tiles that the user can pick from
  public dashPreferencesForm: UntypedFormGroup;
  public homeTabUrl = '';
  userPreferenceLanguage = '';

  constructor(
    private userInfoService: UserInfoService,
    private tileService: TileService,
    private userService: UserService,
    private globalFilterService: GlobalFilterService) {
  }

  ngOnInit(): void {
    // Get user roles and the tiles that the user has access to.
    const userRoles = this.userInfoService.getUserRoles() ?? [];
    this.userSelectableTiles = this.tileService.getUserAccessibleTiles(userRoles);

    // Set up controls for each tile a user has access to
    const tileSelectionControls: { [tileType: string]: UntypedFormControl } = {};
    this.userSelectableTiles.forEach(ust => {
      tileSelectionControls[ust.type] = new UntypedFormControl(true);
    });
    this.dashPreferencesForm = new UntypedFormGroup(tileSelectionControls);

    // Get saved preferences, and initialize any controls that have entries
    this.userService.getDashboardPreferences().subscribe((userDashPreferences: IDashboardPreference) => {
      this.userPreferenceLanguage = userDashPreferences?.userPreferenceLanguage ? userDashPreferences.userPreferenceLanguage : '';
      userDashPreferences?.tiles?.forEach(tile => {
        const control = this.dashPreferencesForm.get(tile.name);
        if (control) {
          control.setValue(tile.isVisible);
        }
      });

      // Sort userSelectableTiles based on saved preferences
      this.userSelectableTiles.sort((tile1, tile2) => {
        const prefTile1 = userDashPreferences?.tiles?.find(tile => tile.name === tile1.type);
        const prefTile2 = userDashPreferences?.tiles?.find(tile => tile.name === tile2.type);
        if (prefTile1 && prefTile2) {
          return prefTile1.sequence - prefTile2.sequence;
        } else if (prefTile1) {
          return -1;
        } else if (prefTile2) {
          return 1;
        } else {
          return 0;
        }
      });
    });

    // Get user's default drAddressId so we can set up a Home link for them
    this.globalFilterService.getDefaultOffice().subscribe((data: IOfficeSelector) => {
      if (data !== null) {
        this.homeTabUrl = `/home/${data.drAddressId}`;
      }
    });
  }

  /**
   * Handle the tile select control changing
   */
  public onTileSelectChange(): void {
    // Get all preferences
    const preferences: IDashboardPreference = {
      tiles: this.userSelectableTiles.map((ust, idx) => {
        return { name: ust.type, isVisible: this.dashPreferencesForm.get(ust.type).value, sequence: idx };
      }),
      userPreferenceLanguage: this.userPreferenceLanguage
    };

    // Save them to user service
    this.updateDashboardPreferences(preferences);
  }

  /**
   * Handle reordering the tiles
   * @param event the event
   */
  public onReorder(event: CdkDragDrop<string[]>): void {
    // Reorder the tiles
    moveItemInArray(this.userSelectableTiles, event.previousIndex, event.currentIndex);

    // Get preferences
    const preferences: IDashboardPreference = {
      tiles: this.userSelectableTiles?.map((ust, idx) => {
        return { name: ust.type, isVisible: this.dashPreferencesForm.get(ust.type).value, sequence: idx };
      }),
      userPreferenceLanguage: this.userPreferenceLanguage
    };

    // Save the preferences
    this.updateDashboardPreferences(preferences);
  }

  /**
   * Update user dashboard preferences
   * @param preferences the user preferences
   */
  private updateDashboardPreferences(preferences: IDashboardPreference) {
    this.userService.getDashboardPreferences().subscribe(prefs => {
      preferences.onlyShowMyRequests = prefs && prefs?.onlyShowMyRequests !== undefined ? prefs?.onlyShowMyRequests : true;
      this.userService.setDashboardPreferences(preferences).subscribe();
    });
  }
}
